import { type ReactNode } from 'react';
import { useMemo } from 'react';

import { Box } from '@hivebrite/design-system/Box';
import { Button } from '@hivebrite/design-system/Button';

import { useTopicContext } from 'hb-react/frontoffice/contexts/TopicContext';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import { Price } from 'hb-react/shared/apps/Events/components/Price';
import { type TicketItem } from 'hb-react/shared/apps/Events/pages/Booking/hooks/useBooking';
import { useBooking } from 'hb-react/shared/apps/Events/pages/Booking/hooks/useBooking';
import { getTicketDisponibility } from 'hb-react/shared/apps/Events/utils/getTicketDisponibility';
import { isEqualCurrency } from 'hb-react/shared/apps/Events/utils/isEqualCurrency';
import { Select } from 'hb-react/shared/components/formik/components/inputs';

const ReadableTicket = ({
  ticket,
  children,
}: {
  children?: ReactNode;
  ticket: TicketItem;
}) => (
  <Box
    flexDirection={{ _: 'column', m: 'row' }}
    flexGrow={2}
    alignItems={{ m: 'center' }}
  >
    <Box flexDirection="column" flexGrow={2}>
      <Box mb={1} alignItems="center">
        <Box font="h4-semibold" dataTestId="ticket-name">
          {ticket.name}
        </Box>
        <Box>
          &nbsp;
          {getTicketDisponibility(
            ticket.quantity_available,
            ticket.unlimited,
            ticket.sold_out,
            ticket.has_expired,
            ticket.limit_reached_for_current_user,
          )}
        </Box>
      </Box>
      <Box>{ticket.description}</Box>
    </Box>
    <Box pl={{ _: 0, m: 6 }} font="h4-semibold" mt={{ _: 1, m: 'unset' }}>
      <Price price={ticket.price} currency={ticket.currency} />
    </Box>
    {children}
  </Box>
);

const BuyableTicket = ({ ticket, index, isLoading }) => {
  const { currentUser } = useDefaultAppContexts();
  const { event, tickets, cart } = useBooking();
  const topicContext = useTopicContext();

  const selectedCurrency = cart?.currency;

  const {
    max_tickets_per_user: maxTicketsPerUser = 0,
    user_total_tickets: userTotalTickets = 0,
    remaining_tickets: remainingTickets = 0,
  } = event || {};

  const [totalSelectedTickets, ticketQuantity] = useMemo(
    () => [
      tickets
        .map(({ cartItem }) => Number(cartItem?.quantity || 0))
        .reduce((total, quantity) => total + quantity, 0),
      Number(
        tickets.find(({ id }) => id === ticket.id)?.cartItem?.quantity || 0,
      ),
    ],
    [tickets, ticket.id],
  );

  const ticketLimit = useMemo(() => {
    if (
      ticket.quantity_available_to_buy < ticket.maximum_per_person ||
      ticket.quantity_available_to_buy < maxTicketsPerUser
    ) {
      return ticket.quantity_available_to_buy;
    }

    if (ticket.maximum_per_person > 0) {
      return (
        ticket.maximum_per_person -
        (ticket.already_bought_for_current_user || 0)
      );
    }

    if (maxTicketsPerUser > 0) {
      return maxTicketsPerUser - userTotalTickets;
    }

    return ticket.quantity_available_to_buy;
  }, [userTotalTickets, maxTicketsPerUser, ticket]);

  const ticketOptions = useMemo(() => {
    const maxTickets =
      maxTicketsPerUser > 0
        ? maxTicketsPerUser - userTotalTickets
        : remainingTickets;

    const available = maxTickets - totalSelectedTickets + ticketQuantity;

    const limit = Math.min(Number(ticketLimit), available, 99);

    return Array.from(
      {
        length: limit + 1,
      },
      (_, i) => (i === 0 ? i.toString() : i),
    );
  }, [
    ticketLimit,
    totalSelectedTickets,
    ticketQuantity,
    maxTicketsPerUser,
    remainingTickets,
    userTotalTickets,
  ]);

  const isAvailableForPurchase = useMemo(() => {
    const isAvailable =
      !ticket.sold_out &&
      !ticket.has_expired &&
      !ticket.limit_reached_for_current_user;

    if (!isAvailable) {
      return false;
    }

    const selectedTickets = tickets.filter(
      ({ cartItem }) => Number(cartItem?.quantity || 0) > 0,
    );

    const freeTickets = selectedTickets.filter(({ isFree }) => isFree);

    if (freeTickets.length === selectedTickets.length) {
      return true;
    }

    const matchCurrency = selectedCurrency
      ? isEqualCurrency(ticket.currency, selectedCurrency)
      : true;

    return matchCurrency || ticket.isFree;
  }, [selectedCurrency, ticket, tickets]);

  if (!cart || !event) {
    return null;
  }

  const topicId = topicContext?.topicId;
  const isWhitelabelGroup = !!topicContext?.whitelabelURL;

  return (
    <Box
      opacity={isAvailableForPurchase ? 1 : 0.5}
      width="100%"
      tabIndex={-1}
      aria-disabled={!isAvailableForPurchase}
    >
      <ReadableTicket ticket={ticket} />
      <Box
        minWidth={{ _: 90, m: 154 }}
        justifyContent="flex-end"
        alignItems="center"
      >
        {currentUser || !ticket.internal_ticket ? (
          <Box
            font="h4-semibold"
            alignItems={{ _: 'flex-start', m: 'center' }}
            ml={{ _: 3, m: 'unset' }}
          >
            <Select
              wrapperProps={{
                mb: 0,
                width: 90,
              }}
              name={`tickets[${index}][cartItem][quantity]`}
              options={ticketOptions}
              placeholder="0"
              disabled={!isAvailableForPurchase || isLoading}
              ariaLabel={s__(
                'NetworkEvent|Select quantity for %{ticketName}',
              ).i__({ ticketName: ticket.name })}
            />
          </Box>
        ) : (
          <Button
            onClick={() => {
              window.location.href =
                // @ts-expect-error: untyped.
                window.Hivebrite.rootUrl +
                (topicId && !isWhitelabelGroup
                  ? Routes.network_login_path({
                      params: {
                        redirect_to:
                          Routes.topic_event_booking_ticket_selection_path(
                            topicId,
                            event.id,
                            cart.id,
                          ),
                      },
                    })
                  : Routes.network_login_path({
                      params: {
                        redirect_to:
                          Routes.network_events_event_booking_ticket_selection_path(
                            event.id,
                            cart.id,
                          ),
                      },
                    }));
            }}
          >
            {__('Login')}
          </Button>
        )}
      </Box>
    </Box>
  );
};

export const Ticket = ({
  readOnly = false,
  ...props
}: {
  index: number;
  isLoading: boolean;
  readOnly?: boolean;
  ticket: TicketItem;
}) => (readOnly ? <ReadableTicket {...props} /> : <BuyableTicket {...props} />);
